import React, { useMemo, useEffect, useState } from 'react';
import { NewsImpactAnalysisWidget } from './NewsImpactAnalysisWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  selectedSectorFilterAtom,
  portfolioIdAtom,
} from '~/atoms/dashboardAtom';
import {
  GET_AI_ANALYSIS,
  GET_AI_ANALYSIS_ALL_SECTORS,
  GET_AI_ANALYSIS_WITH_PORTFOLIO_ID,
  GET_PORTFOLIO_DATA,
} from '~/graphql/queries/portfolio';
import { GET_INSTRUMENTS_BY_TICKERS } from '~/graphql/queries/instruments';

interface Instrument {
  id: string;
  ticker: string;
  full_ticker?: string;
  name: string;
  isin: string;
  sector?: string;
  currency?: string;
  exchange?: string;
  companyId: string;
}

interface SectorNews {
  title: string;
  sectorname: string;
  sentiment_analysis: {
    trends: string[];
    confidence: number;
    impact_score: number;
    sector_impact: string;
    sentiment_score: number;
    duration_forecast: string;
    affected_instruments: string[];
  };
}

interface AIAnalysisResponse {
  EQSectorNews: SectorNews[];
  EQPortfoliosAIAnalysis: {
    analysis_data?: Record<string, unknown> | null;
    createdAt?: string | null;
  }[];
}

export interface NewsImpactAnalysisWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const NewsImpactAnalysisWidgetWrapper: React.FC<
  NewsImpactAnalysisWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const selectedSectorFilter = useAtomValue(selectedSectorFilterAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const snapshotId = latestSnapshotId;

  // State to track if we should use fallback query
  const [useFallback, setUseFallback] = useState(false);

  // Determine which query to use based on portfolio ID availability and fallback state
  const queryConfig = useMemo(() => {
    // If we have a portfolio ID and haven't tried fallback yet, use portfolio-specific query
    if (portfolioId && !useFallback) {
      return {
        query: GET_AI_ANALYSIS_WITH_PORTFOLIO_ID,
        variables: { snapshotId, portfolioId, sectors: selectedSectorFilter },
      };
    }

    // Fallback to sector-based filtering
    if (selectedSectorFilter.length > 0) {
      return {
        query: GET_AI_ANALYSIS,
        variables: { snapshotId, sectors: selectedSectorFilter },
      };
    }

    // If no sectors selected, get all sectors
    return {
      query: GET_AI_ANALYSIS_ALL_SECTORS,
      variables: { snapshotId },
    };
  }, [portfolioId, useFallback, snapshotId, selectedSectorFilter]);

  const { query, variables } = queryConfig;

  const { data, loading, error, refetch } = useQuery<AIAnalysisResponse>(
    query,
    {
      variables,
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId,
      onCompleted: (data) => {
        // If we used portfolio-specific query and got no results, trigger fallback
        // Only trigger fallback once to prevent infinite loops
        if (
          portfolioId &&
          !useFallback &&
          (!data?.EQSectorNews || data.EQSectorNews.length === 0)
        ) {
          setUseFallback(true);
        }
      },
      onError: (_error) => {
        // If portfolio-specific query fails, trigger fallback
        // Only trigger fallback once to prevent infinite loops
        if (portfolioId && !useFallback) {
          setUseFallback(true);
        }
      },
    },
  );

  // Extract all tickers from news data
  const allTickers = useMemo(() => {
    if (!data?.EQSectorNews) return [];
    const tickers = new Set<string>();
    data.EQSectorNews.forEach((news) => {
      news.sentiment_analysis?.affected_instruments?.forEach((ticker) => {
        if (ticker && ticker.trim()) {
          tickers.add(ticker.trim());
        }
      });
    });
    return Array.from(tickers);
  }, [data?.EQSectorNews]);

  // Query instruments for all tickers
  const instrumentVariables = useMemo(() => {
    return {
      tickers: allTickers, // Allow up to 20 tickers
    };
  }, [allTickers]);

  const shouldSkipInstrumentQuery =
    allTickers.length === 0 ||
    !allTickers.some((ticker) => ticker && ticker.trim() !== '');

  const { data: instrumentsData } = useQuery(GET_INSTRUMENTS_BY_TICKERS, {
    variables: instrumentVariables,
    skip: shouldSkipInstrumentQuery,
    notifyOnNetworkStatusChange: true,
  });

  // Fetch portfolio allocation data to check if tickers have allocation > 0
  const { data: portfolioData } = useQuery(GET_PORTFOLIO_DATA, {
    variables: { snapshotId },
    skip: !snapshotId,
    notifyOnNetworkStatusChange: true,
  });

  // Create a map of ticker -> allocation percentage
  const tickerAllocationMap = useMemo(() => {
    const map = new Map<string, number>();
    if (portfolioData?.EQPortfolioAllocation) {
      portfolioData.EQPortfolioAllocation.forEach(
        (allocation: {
          current_percent?: number | null;
          proposed_percent?: number | null;
          EQInstrument?: {
            full_ticker?: string | null;
          } | null;
        }) => {
          const ticker = allocation.EQInstrument?.full_ticker;
          if (ticker) {
            // Use current_percent (current allocation) only
            const percent = allocation.current_percent ?? 0;
            // Normalize ticker (handle formats like "AAPL:NASDAQ" -> "AAPL")
            const baseTicker =
              ticker.split(':')[0] ||
              ticker.split('.')[0] ||
              ticker.split(' ')[0];
            map.set(baseTicker.toUpperCase(), percent);
            // Also store full ticker if different
            if (baseTicker !== ticker) {
              map.set(ticker.toUpperCase(), percent);
            }
          }
        },
      );
    }
    return map;
  }, [portfolioData]);

  // Automatically refetch when snapshotId changes
  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  // Reset fallback state when portfolio ID changes
  useEffect(() => {
    setUseFallback(false);
  }, [portfolioId]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  // Transform sector news data to match widget format
  const newsImpacts = useMemo(() => {
    if (loading || !data?.EQSectorNews) {
      return [];
    }

    const instruments = instrumentsData?.EQInstrument || [];

    return data.EQSectorNews.map((news) => {
      const affectedTickers =
        news.sentiment_analysis?.affected_instruments || [];

      // Map tickers to include company names and allocation info
      const tickersWithCompanyNames = affectedTickers.map((ticker) => {
        const instrument = instruments.find(
          (inst: Instrument) =>
            inst.full_ticker?.startsWith(ticker) || inst.ticker === ticker,
        );

        // Check if ticker has allocation > 0
        const normalizedTicker = ticker.toUpperCase().trim();
        const baseTicker =
          normalizedTicker.split(':')[0] ||
          normalizedTicker.split('.')[0] ||
          normalizedTicker.split(' ')[0];
        const allocation =
          tickerAllocationMap.get(normalizedTicker) ??
          tickerAllocationMap.get(baseTicker) ??
          0;
        const hasAllocation = allocation > 0;

        return {
          ticker,
          companyName: instrument?.name || null,
          hasAllocation,
        };
      });

      return {
        title: news.title,
        affectedTickers: tickersWithCompanyNames,
        impact: `${(news.sentiment_analysis?.impact_score || 0) > 0 ? '+' : ''}${(news.sentiment_analysis?.impact_score || 0).toFixed(1)}%`,
        isPositive: (news.sentiment_analysis?.impact_score || 0) > 0,
        sector: news.sectorname,
      };
    });
  }, [data, loading, instrumentsData, tickerAllocationMap]);

  // Format and validate timestamp
  const formattedTimestamp = useMemo(() => {
    const timestamp = data?.EQPortfoliosAIAnalysis?.[0]?.createdAt;

    if (!timestamp) return 'N/A';

    try {
      // This matches ISO 8601 format with timezone indicators
      const hasTimezoneInfo = /[+-]\d{2}:\d{2}$|Z$/i.test(timestamp);

      // If no timezone info, assume it's UTC and add Z
      const utcTimestamp = hasTimezoneInfo ? timestamp : `${timestamp}Z`;

      const date = new Date(utcTimestamp);

      // Check if it's a valid date
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format according to user's local timezone and time format preference
      // Use the browser's default time format (respects user's system settings)
      const formatted = date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        // hour12: undefined - let the browser decide based on user's locale/system settings
      });

      return formatted;
    } catch (error) {
      return 'Invalid Date';
    }
  }, [data]);

  return (
    <NewsImpactAnalysisWidget
      newsImpacts={newsImpacts}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={shortDescription}
      timestamp={formattedTimestamp}
      snapshotId={snapshotId}
    />
  );
};
